import React, { useEffect, useState } from 'react';
import Carousel, { ArrowProps } from 'react-multi-carousel';
import Image from 'next/legacy/image';

import { ITestimonialSliderFields } from '../../../types/contentful';

import { GreenlightTheme } from '../../theme/themes/greenlight/index';
import { breakpoints } from '../../styles/media-queries';

import { Testimonial } from './Testimonial';
import { ScrollReveal } from '../ScrollReveal';

import angleLeft from '@/images/angle-left.svg';
import angleRight from '@/images/angle-right.svg';

import 'react-multi-carousel/lib/styles.css';

type SliderArrowDirection = 'left' | 'right';

interface CustomArrowProps extends ArrowProps {
  icon: string;
  direction: SliderArrowDirection;
}

const colors = [
  GreenlightTheme.color.sliderSlide1,
  GreenlightTheme.color.sliderSlide2,
  GreenlightTheme.color.sliderSlide3
];

const CustomArrow = ({ onClick, direction, icon }: CustomArrowProps) => {
  return (
    <button
      type="button"
      className={`bg-transparent absolute appearance-none border-0 ${
        direction === 'left'
          ? 'left-[-2.125rem] right-auto'
          : 'left-auto right-[-2.125rem]'
      }`}
      onClick={onClick}
    >
      <Image src={icon} alt={`slider-arrow-${direction}`} />
    </button>
  );
};

export function TestimonialSliderBlock({
  testimonials,
  headingText
}: ITestimonialSliderFields) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(
      typeof window !== `undefined` && window.innerWidth < breakpoints.tablet
    );
  }, []);

  return isMobile ? (
    <div className="my-12 overflow-hidden" data-blockid="testimonial-slider">
      <h2 className="heading-2 testimonial-slider-heading text-black">
        {headingText ?? 'What parents are saying'}
      </h2>
      <div className="testimonial-carousel-container">
        <Carousel
          responsive={{
            s: {
              breakpoint: { max: breakpoints.tablet, min: 0 },
              items: 1
            },
            m: {
              breakpoint: {
                max: breakpoints.desktop,
                min: breakpoints.tablet + 1
              },
              items: 2
            }
          }}
          swipeable={false}
          draggable={false}
          infinite={true}
          customRightArrow={<CustomArrow direction="right" icon={angleRight} />}
          customLeftArrow={<CustomArrow direction="left" icon={angleLeft} />}
        >
          {testimonials!.map(({ fields }, i) => (
            <Testimonial {...fields} key={i} color={colors[i]} />
          ))}
        </Carousel>
      </div>
    </div>
  ) : (
    <ScrollReveal>
      <div
        className="block-container mb-16 mt-16"
        data-blockid="testimonialSlider"
        data-testid="desktop-testimonial-slider"
      >
        <div className="max-w-screen-[1350px] w-full px-3 tablet:px-12">
          <div className="layout w-full !items-stretch desktop:py-10 [&>*]:px-1 [&>*]:max:px-4">
            <div className="box-column mb-4 flex w-full justify-center">
              <h2 className="heading-2 mb-8 text-black">
                {headingText ?? 'What parents are saying'}
              </h2>
            </div>
            {testimonials!.map(({ fields }, i) => (
              <div className="box-column flex w-1/3 flex-col" key={i}>
                <Testimonial
                  {...fields}
                  color={colors[i]}
                  data-testid="testimonial-block-component"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
}
